import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getCurrentUserData, updateUserProfile, changePassword, updateUserStatistics } from '../services/authService';
import { refreshExchangeRates, getCacheAge } from '../services/currencyService';
import { getUserMembers, addUserMember, updateUserMember, deleteUserMember } from '../services/memberService';
import CountryAutocompleteInput from './CountryAutocompleteInput';

const UserSettingsModal = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile state
  const [citizenships, setCitizenships] = useState([]);
  const [newCitizenship, setNewCitizenship] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [showInsightTodos, setShowInsightTodos] = useState(true);
  const [allowUsernameSharing, setAllowUsernameSharing] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [workEmails, setWorkEmails] = useState([]);
  const [newWorkEmail, setNewWorkEmail] = useState('');

  // Exchange rate state
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [exchangeRateStatus, setExchangeRateStatus] = useState('');

  // Password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Travelers/Members state
  const [savedMembers, setSavedMembers] = useState([]);
  const [editingMember, setEditingMember] = useState(null);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberEmail, setNewMemberEmail] = useState('');

  useEffect(() => {
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    if (!user?.uid) return;

    const result = await getCurrentUserData(user.uid);
    if (result.success && result.data) {
      setCitizenships(result.data.citizenships || []);
      setPreferredCurrency(result.data.preferredCurrency || 'USD');
      setShowInsightTodos(result.data.showInsightTodos !== false); // Default to true
      setAllowUsernameSharing(result.data.allowUsernameSharing !== false); // Default to true
      setStatistics(result.data.statistics || null);
      setWorkEmails(result.data.workEmails || []);
    }

    // Update exchange rate status
    updateExchangeRateStatus();

    // Load saved members when travelers tab is active
    if (activeTab === 'travelers') {
      loadSavedMembers();
    }
  };

  const loadSavedMembers = async () => {
    if (!user?.uid) return;

    try {
      const members = await getUserMembers(user.uid);
      setSavedMembers(members);
    } catch (error) {
      console.error('Error loading saved members:', error);
      setMessage({ type: 'error', text: 'Failed to load saved travelers' });
    }
  };

  const handleAddMember = async () => {
    if (!newMemberName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a traveler name' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const newMember = await addUserMember(user.uid, {
        name: newMemberName.trim(),
        email: newMemberEmail.trim() || null
      });

      setSavedMembers([...savedMembers, newMember]);
      setNewMemberName('');
      setNewMemberEmail('');
      setMessage({ type: 'success', text: 'Traveler added successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error adding member:', error);
      setMessage({ type: 'error', text: 'Failed to add traveler' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMember = async (memberId, updates) => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateUserMember(user.uid, memberId, updates);
      setSavedMembers(savedMembers.map(m =>
        m.id === memberId ? { ...m, ...updates } : m
      ));
      setEditingMember(null);
      setMessage({ type: 'success', text: 'Traveler updated successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error updating member:', error);
      setMessage({ type: 'error', text: 'Failed to update traveler' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!confirm('Are you sure you want to delete this traveler from your library?')) {
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await deleteUserMember(user.uid, memberId);
      setSavedMembers(savedMembers.filter(m => m.id !== memberId));
      setMessage({ type: 'success', text: 'Traveler deleted successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting member:', error);
      setMessage({ type: 'error', text: 'Failed to delete traveler' });
    } finally {
      setLoading(false);
    }
  };

  // Load saved members when switching to travelers tab
  useEffect(() => {
    if (activeTab === 'travelers') {
      loadSavedMembers();
    }
  }, [activeTab]);

  const handleRefreshStatistics = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    const result = await updateUserStatistics();

    setLoading(false);

    if (result.success) {
      setStatistics(result.statistics);
      setMessage({ type: 'success', text: 'Statistics updated successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to update statistics' });
    }
  };

  const updateExchangeRateStatus = () => {
    const age = getCacheAge();
    if (age === null) {
      setExchangeRateStatus('Never updated');
    } else {
      const hours = Math.floor(age / (1000 * 60 * 60));
      const minutes = Math.floor((age % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 0) {
        setExchangeRateStatus(`Updated ${hours}h ${minutes}m ago`);
      } else if (minutes > 0) {
        setExchangeRateStatus(`Updated ${minutes}m ago`);
      } else {
        setExchangeRateStatus('Updated just now');
      }
    }
  };

  const handleRefreshExchangeRates = async () => {
    setExchangeRateLoading(true);
    setExchangeRateStatus('Fetching latest rates...');

    const success = await refreshExchangeRates();

    setExchangeRateLoading(false);

    if (success) {
      setMessage({ type: 'success', text: 'Exchange rates updated successfully' });
      updateExchangeRateStatus();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: 'Failed to update exchange rates. Using fallback rates.' });
      setExchangeRateStatus('Using fallback rates');
    }
  };

  const handleAddCitizenship = async (country) => {
    // If country is provided directly (from autocomplete), use it
    // Otherwise use the newCitizenship state (for Enter key)
    const countryToAdd = country || newCitizenship.trim();

    if (!countryToAdd) {
      setMessage({ type: 'error', text: 'Please select a country' });
      return;
    }

    if (citizenships.includes(countryToAdd)) {
      setMessage({ type: 'error', text: 'This citizenship is already added' });
      setNewCitizenship('');
      return;
    }

    const updatedCitizenships = [...citizenships, countryToAdd];

    setLoading(true);
    const result = await updateUserProfile(user.uid, {
      citizenships: updatedCitizenships
    });
    setLoading(false);

    if (result.success) {
      setCitizenships(updatedCitizenships);
      setNewCitizenship('');
      setMessage({ type: 'success', text: 'Citizenship added successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to add citizenship' });
    }
  };

  const handleRemoveCitizenship = async (citizenship) => {
    const updatedCitizenships = citizenships.filter(c => c !== citizenship);

    setLoading(true);
    const result = await updateUserProfile(user.uid, {
      citizenships: updatedCitizenships
    });
    setLoading(false);

    if (result.success) {
      setCitizenships(updatedCitizenships);
      setMessage({ type: 'success', text: 'Citizenship removed successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to remove citizenship' });
    }
  };

  const handleAddWorkEmail = async () => {
    const emailToAdd = newWorkEmail.trim().toLowerCase();

    if (!emailToAdd) {
      setMessage({ type: 'error', text: 'Please enter an email address' });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToAdd)) {
      setMessage({ type: 'error', text: 'Please enter a valid email address' });
      return;
    }

    // Check if email is already the primary email
    if (emailToAdd === user?.email?.toLowerCase()) {
      setMessage({ type: 'error', text: 'This is already your primary email' });
      return;
    }

    if (workEmails.includes(emailToAdd)) {
      setMessage({ type: 'error', text: 'This work email is already added' });
      setNewWorkEmail('');
      return;
    }

    const updatedWorkEmails = [...workEmails, emailToAdd];

    setLoading(true);
    const result = await updateUserProfile(user.uid, {
      workEmails: updatedWorkEmails
    });
    setLoading(false);

    if (result.success) {
      setWorkEmails(updatedWorkEmails);
      setNewWorkEmail('');
      setMessage({ type: 'success', text: 'Work email added successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to add work email' });
    }
  };

  const handleRemoveWorkEmail = async (email) => {
    const updatedWorkEmails = workEmails.filter(e => e !== email);

    setLoading(true);
    const result = await updateUserProfile(user.uid, {
      workEmails: updatedWorkEmails
    });
    setLoading(false);

    if (result.success) {
      setWorkEmails(updatedWorkEmails);
      setMessage({ type: 'success', text: 'Work email removed successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to remove work email' });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Please fill in all password fields' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
    setLoading(false);

    if (result.success) {
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to change password' });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">User Settings</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-blue-700 rounded-full p-2 transition"
            title="Close"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex">
            <button
              onClick={() => setActiveTab('profile')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'profile'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-white'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <i className="fas fa-user mr-2"></i>
              Profile & Citizenship
            </button>
            <button
              onClick={() => setActiveTab('travelers')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'travelers'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-white'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <i className="fas fa-users mr-2"></i>
              Travelers
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`px-6 py-3 font-medium transition ${
                activeTab === 'password'
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-white'
                  : 'text-gray-600 hover:text-blue-600'
              }`}
            >
              <i className="fas fa-lock mr-2"></i>
              Change Password
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6" style={{ overflow: 'visible' }}>
          <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Message */}
            {message.text && (
              <div
                className={`mb-4 p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {message.text}
              </div>
            )}

          {/* Profile & Citizenship Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-3">Account Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-gray-800">{user?.email}</span>
                  </div>
                  {user?.displayName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Display Name:</span>
                      <span className="font-medium text-gray-800">{user.displayName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* User Statistics */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-800">
                    <i className="fas fa-chart-bar mr-2 text-blue-600"></i>
                    Your Travel Statistics
                  </h3>
                  <button
                    onClick={handleRefreshStatistics}
                    disabled={loading}
                    className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                    title="Refresh statistics"
                  >
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                  </button>
                </div>
                {statistics ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">{statistics.tripCount || 0}</div>
                      <div className="text-xs text-gray-600 mt-1">Total Trips</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600">{statistics.publicItineraryCount || 0}</div>
                      <div className="text-xs text-gray-600 mt-1">Public Itineraries</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">{statistics.totalCountries || 0}</div>
                      <div className="text-xs text-gray-600 mt-1">Countries Visited</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-600 text-sm mb-2">No statistics available yet</p>
                    <button
                      onClick={handleRefreshStatistics}
                      disabled={loading}
                      className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
                    >
                      Calculate Statistics
                    </button>
                  </div>
                )}
              </div>

              {/* Currency Preference */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  <i className="fas fa-dollar-sign mr-2 text-blue-600"></i>
                  Preferred Currency
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Select your preferred currency. All booking prices will be converted to this currency.
                </p>
                <div className="flex items-center gap-3">
                  <select
                    value={preferredCurrency}
                    onChange={async (e) => {
                      const newCurrency = e.target.value;
                      setPreferredCurrency(newCurrency);
                      setLoading(true);
                      const result = await updateUserProfile(user.uid, {
                        preferredCurrency: newCurrency
                      });
                      setLoading(false);
                      if (result.success) {
                        setMessage({ type: 'success', text: 'Currency preference updated successfully' });
                        setTimeout(() => setMessage({ type: '', text: '' }), 3000);

                        // Dispatch event to notify other components to refresh
                        window.dispatchEvent(new CustomEvent('currencyChanged', {
                          detail: { currency: newCurrency }
                        }));
                      } else {
                        setMessage({ type: 'error', text: result.error || 'Failed to update currency preference' });
                      }
                    }}
                    className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                    style={{ color: '#111827' }}
                    disabled={loading}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="CHF">CHF - Swiss Franc</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="MXN">MXN - Mexican Peso</option>
                    <option value="BRL">BRL - Brazilian Real</option>
                    <option value="ZAR">ZAR - South African Rand</option>
                    <option value="NZD">NZD - New Zealand Dollar</option>
                    <option value="SGD">SGD - Singapore Dollar</option>
                    <option value="HKD">HKD - Hong Kong Dollar</option>
                    <option value="KRW">KRW - South Korean Won</option>
                    <option value="SEK">SEK - Swedish Krona</option>
                    <option value="NOK">NOK - Norwegian Krone</option>
                    <option value="DKK">DKK - Danish Krone</option>
                    <option value="PLN">PLN - Polish Zloty</option>
                    <option value="THB">THB - Thai Baht</option>
                    <option value="IDR">IDR - Indonesian Rupiah</option>
                    <option value="MYR">MYR - Malaysian Ringgit</option>
                    <option value="PHP">PHP - Philippine Peso</option>
                    <option value="CZK">CZK - Czech Koruna</option>
                    <option value="HUF">HUF - Hungarian Forint</option>
                    <option value="ILS">ILS - Israeli Shekel</option>
                    <option value="CLP">CLP - Chilean Peso</option>
                    <option value="AED">AED - UAE Dirham</option>
                    <option value="SAR">SAR - Saudi Riyal</option>
                  </select>
                  <span className="text-gray-600 font-medium">{preferredCurrency}</span>
                </div>

                {/* Exchange Rate Status and Refresh */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-xs text-gray-600 mb-1">Exchange Rates</p>
                      <p className="text-sm font-medium text-gray-800">
                        <i className="fas fa-clock mr-1 text-blue-600"></i>
                        {exchangeRateStatus || 'Loading...'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Real-time rates cached for 24 hours
                      </p>
                    </div>
                    <button
                      onClick={handleRefreshExchangeRates}
                      disabled={exchangeRateLoading || loading}
                      className="ml-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    >
                      {exchangeRateLoading ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Updating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-sync-alt mr-2"></i>
                          Update Now
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Insight Todos Toggle */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  <i className="fas fa-lightbulb mr-2 text-blue-600"></i>
                  Insight Todos
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Enable helpful suggestions and reminders for your trips (e.g., booking hotels, malaria medication).
                </p>
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showInsightTodos}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setShowInsightTodos(newValue);
                        setLoading(true);
                        const result = await updateUserProfile(user.uid, {
                          showInsightTodos: newValue
                        });
                        setLoading(false);
                        if (result.success) {
                          setMessage({ type: 'success', text: 'Insight todos preference updated successfully' });
                          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                        } else {
                          setMessage({ type: 'error', text: result.error || 'Failed to update preference' });
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">
                    {showInsightTodos ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Username Sharing Toggle */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  <i className="fas fa-user-plus mr-2 text-blue-600"></i>
                  Username-Based Trip Sharing
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Allow other users to share trips with you by your username. When enabled, you will receive in-app notifications when someone shares a trip with you.
                </p>
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowUsernameSharing}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setAllowUsernameSharing(newValue);
                        setLoading(true);
                        const result = await updateUserProfile(user.uid, {
                          allowUsernameSharing: newValue
                        });
                        setLoading(false);
                        if (result.success) {
                          setMessage({ type: 'success', text: 'Username sharing preference updated successfully' });
                          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                        } else {
                          setMessage({ type: 'error', text: result.error || 'Failed to update preference' });
                        }
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                  <span className="text-sm text-gray-700">
                    {allowUsernameSharing ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              {/* Work Emails Management */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  <i className="fas fa-envelope mr-2 text-blue-600"></i>
                  Work Emails for Booking Import
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add work emails to forward booking confirmations to <strong>bookings@travaili.com</strong> for easy import.
                </p>

                {/* Current Work Emails */}
                {workEmails.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {workEmails.map((email, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <i className="fas fa-envelope text-green-600"></i>
                          <span className="font-medium text-gray-800">{email}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveWorkEmail(email)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full p-2 transition"
                          title="Remove work email"
                        >
                          <i className="fas fa-trash text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Work Email */}
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newWorkEmail}
                    onChange={(e) => setNewWorkEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddWorkEmail();
                      }
                    }}
                    placeholder="work@company.com"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loading}
                  />
                  <button
                    onClick={handleAddWorkEmail}
                    disabled={loading || !newWorkEmail.trim()}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Add
                  </button>
                </div>

                {workEmails.length === 0 && (
                  <p className="text-sm text-gray-500 italic mt-4">
                    No work emails added yet. Add a work email to forward bookings from your work account.
                  </p>
                )}
              </div>

              {/* Citizenship Management */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">
                  <i className="fas fa-passport mr-2 text-blue-600"></i>
                  Citizenships
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Add your citizenships to get accurate visa requirements for your trips.
                </p>

                {/* Current Citizenships */}
                {citizenships.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {citizenships.map((citizenship, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <i className="fas fa-flag text-blue-600"></i>
                          <span className="font-medium text-gray-800">{citizenship}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveCitizenship(citizenship)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full p-2 transition"
                          title="Remove citizenship"
                        >
                          <i className="fas fa-trash text-sm"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Citizenship */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <CountryAutocompleteInput
                      value={newCitizenship}
                      onChange={setNewCitizenship}
                      onSelect={handleAddCitizenship}
                      placeholder="Type to search countries (e.g., United States)"
                      disabled={loading}
                      excludeCountries={citizenships}
                    />
                  </div>
                  <button
                    onClick={() => handleAddCitizenship()}
                    disabled={loading || !newCitizenship.trim()}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Add
                  </button>
                </div>

                {citizenships.length === 0 && (
                  <p className="text-sm text-gray-500 italic mt-4">
                    No citizenships added yet. Add your citizenship to enable visa requirement checks.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Travelers Tab */}
          {activeTab === 'travelers' && (
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">
                  <i className="fas fa-users mr-2 text-blue-600"></i>
                  Saved Travelers
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Manage your saved travelers. These can be quickly added to any trip without re-entering their information.
                </p>

                {/* Saved Members List */}
                {savedMembers.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {savedMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3"
                      >
                        {editingMember?.id === member.id ? (
                          /* Edit Mode */
                          <div className="flex-1 flex items-center gap-2">
                            <input
                              type="text"
                              value={editingMember.name}
                              onChange={(e) =>
                                setEditingMember({ ...editingMember, name: e.target.value })
                              }
                              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                              placeholder="Name"
                            />
                            <input
                              type="email"
                              value={editingMember.email || ''}
                              onChange={(e) =>
                                setEditingMember({ ...editingMember, email: e.target.value })
                              }
                              className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm"
                              placeholder="Email (optional)"
                            />
                            <div className="flex gap-1">
                              <button
                                onClick={() =>
                                  handleUpdateMember(member.id, {
                                    name: editingMember.name,
                                    email: editingMember.email || null
                                  })
                                }
                                disabled={!editingMember.name.trim() || loading}
                                className="text-green-600 hover:text-green-800 hover:bg-green-100 rounded-full p-2 transition disabled:opacity-50"
                                title="Save"
                              >
                                <i className="fas fa-check text-sm"></i>
                              </button>
                              <button
                                onClick={() => setEditingMember(null)}
                                disabled={loading}
                                className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full p-2 transition"
                                title="Cancel"
                              >
                                <i className="fas fa-times text-sm"></i>
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <>
                            <div className="flex items-center gap-3 flex-1">
                              <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                {member.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-800">{member.name}</div>
                                {member.email && (
                                  <div className="text-sm text-gray-600">{member.email}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setEditingMember({ ...member })}
                                disabled={loading}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full p-2 transition"
                                title="Edit traveler"
                              >
                                <i className="fas fa-edit text-sm"></i>
                              </button>
                              <button
                                onClick={() => handleDeleteMember(member.id)}
                                disabled={loading}
                                className="text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full p-2 transition"
                                title="Delete traveler"
                              >
                                <i className="fas fa-trash text-sm"></i>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic mb-4">
                    No saved travelers yet. Add travelers below to reuse them across your trips.
                  </p>
                )}

                {/* Add New Member Form */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-800 mb-3 text-sm">
                    <i className="fas fa-plus-circle mr-2 text-blue-600"></i>
                    Add New Traveler
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={newMemberName}
                        onChange={(e) => setNewMemberName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newMemberName.trim()) {
                            e.preventDefault();
                            handleAddMember();
                          }
                        }}
                        placeholder="Enter traveler name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Email (optional)
                      </label>
                      <input
                        type="email"
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newMemberName.trim()) {
                            e.preventDefault();
                            handleAddMember();
                          }
                        }}
                        placeholder="Enter email address"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        disabled={loading}
                      />
                    </div>
                    <button
                      onClick={handleAddMember}
                      disabled={!newMemberName.trim() || loading}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition text-sm"
                    >
                      {loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2"></i>
                          Adding...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus mr-2"></i>
                          Add Traveler
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, newPassword: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={loading}
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Changing Password...
                  </>
                ) : (
                  <>
                    <i className="fas fa-key mr-2"></i>
                    Change Password
                  </>
                )}
              </button>
            </form>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

UserSettingsModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UserSettingsModal;
